<template>
  <section v-if="validation && (hasIssues || validation.lines.length)" class="mb-3">
    <v-alert
      v-if="maxLeadTime"
      type="info"
      border="left"
      text
      dense
      icon="mdi-timer-sand"
      class="mb-2"
    >
      <div>
        <b>Mayor tiempo de entrega:</b> {{ maxLeadTime.days }} días ·
        {{ maxLeadTime.code }} {{ maxLeadTime.description }}
        <span v-if="!maxLeadTime.shortfall" class="text-caption">(hay stock en CD, no requiere compra)</span>
      </div>
      <div v-if="maxPurchaseLeadTime && maxPurchaseLeadTime.code !== maxLeadTime.code">
        <b>Mayor entre los que se compran:</b> {{ maxPurchaseLeadTime.days }} días ·
        {{ maxPurchaseLeadTime.code }} {{ maxPurchaseLeadTime.description }}
      </div>
      <div v-if="maxPurchaseLeadTime">
        Si se compra hoy, lo último llegaría el <b>{{ formatIsoDate(purchaseArrival) }}</b>
        <span
          v-if="desiredDispatchDate && purchaseArrival > desiredDispatchDate"
          class="orange--text text--darken-4 font-weight-bold"
        >
          · después del despacho deseado ({{ formatIsoDate(desiredDispatchDate) }})
        </span>
        <span v-else-if="desiredDispatchDate" class="green--text text--darken-2">
          · antes del despacho deseado ({{ formatIsoDate(desiredDispatchDate) }})
        </span>
      </div>
    </v-alert>
    <v-alert
      v-if="validation.missing_main_equipment.length"
      type="error"
      border="left"
      text
      dense
      class="mb-2"
    >
      Falta equipo principal en la cubicación:
      <b>{{ validation.missing_main_equipment.join(", ") }}</b>. Al enviar se
      pedirá confirmar que la solicitud va sin ellos.
    </v-alert>
    <v-alert
      v-if="validation.inactive_materials.length"
      type="error"
      border="left"
      text
      dense
      class="mb-2"
    >
      Materiales inactivos en SAP (la solicitud no se podrá enviar con ellos):
      <b>{{ validation.inactive_materials.map((item) => item.code).join(", ") }}</b>
      <div
        v-for="item in validation.inactive_materials.filter((entry) => entry.substitutes.length)"
        :key="`inactive-${item.code}`"
        class="d-flex align-center flex-wrap mt-2"
      >
        <span class="mr-2">{{ item.code }} se puede reemplazar por:</span>
        <!-- light: a typed v-alert forces the dark theme on its children (and the dropdown) -->
        <v-select
          v-model="selectedSubstitutes[item.code]"
          :items="item.substitutes"
          :item-text="substituteText"
          item-value="code"
          dense
          hide-details
          light
          class="substitute-select mr-2"
        >
          <!-- Own colors: inside the alert the text would take its orange/red tone -->
          <template v-slot:item="{ item: option }">
            <span class="substitute-option">
              <b class="substitute-option__code">{{ option.code }}</b>
              <span class="substitute-option__desc">{{ option.description }}</span>
              <span :class="option.covers ? 'substitute-option__stock--ok' : 'substitute-option__stock--short'"
                >stock {{ option.available }}{{ option.covers ? "" : " · no alcanza" }}</span
              >
            </span>
          </template>
          <template v-slot:selection="{ item: option }">
            <span class="substitute-option">
              <b class="substitute-option__code">{{ option.code }}</b>
              <span class="substitute-option__desc">{{ option.description }}</span>
              <span :class="option.covers ? 'substitute-option__stock--ok' : 'substitute-option__stock--short'"
                >stock {{ option.available }}{{ option.covers ? "" : " · no alcanza" }}</span
              >
            </span>
          </template>
        </v-select>
        <v-btn
          x-small
          color="blue darken-2"
          dark
          :loading="substituting === item.code"
          @click="substitute(item)"
        >
          Reemplazar
        </v-btn>
      </div>
    </v-alert>
    <v-alert
      v-if="validation.over_stock.length"
      type="warning"
      border="left"
      text
      dense
      class="mb-2 over-stock-alert"
    >
      <div class="d-flex align-center flex-wrap">
        <span
          >{{ validation.over_stock.length }}
          {{ validation.over_stock.length === 1 ? "material irá" : "materiales irán" }}
          a compra por falta de stock.</span
        >
        <v-btn x-small text light class="ml-2" @click="showOverStock = !showOverStock">
          {{ showOverStock ? "Ocultar" : "Ver detalle" }}
        </v-btn>
        <v-btn
          v-if="validation.recommended_substitutions.length"
          small
          color="blue darken-2"
          class="text-white ml-auto"
          :loading="applyingRecommended"
          @click="applyRecommended"
        >
          Aplicar sustitutos recomendados ({{ validation.recommended_substitutions.length }})
        </v-btn>
      </div>
      <v-simple-table v-if="showOverStock" dense light class="mt-2 transparent">
        <thead>
          <tr>
            <th>Código</th>
            <th>Descripción</th>
            <th class="text-right">Solicitado</th>
            <th class="text-right">Stock disponible</th>
            <th class="text-right">A compra</th>
            <th>Sustituto con stock</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in validation.over_stock" :key="item.code">
            <td>{{ item.code }}</td>
            <td>{{ item.description }}</td>
            <td class="text-right">{{ item.requested }}</td>
            <td class="text-right">{{ item.available }}</td>
            <td class="text-right">{{ item.shortfall }}</td>
            <td>
              <!-- Only substitutes whose stock covers the line: one without stock would also go
                   to purchase, and the recommended-substitutes button counts these same ones -->
              <div v-if="coveringSubstitutes(item).length" class="d-flex align-center">
                <v-select
                  v-model="selectedSubstitutes[item.code]"
                  :items="coveringSubstitutes(item)"
                  :item-text="substituteText"
                  item-value="code"
                  dense
                  hide-details
                  light
                  class="substitute-select mr-2"
                >
                  <!-- Own colors: inside the alert the text would take its orange/red tone -->
                  <template v-slot:item="{ item: option }">
                    <span class="substitute-option">
                      <b class="substitute-option__code">{{ option.code }}</b>
                      <span class="substitute-option__desc">{{ option.description }}</span>
                      <span :class="option.covers ? 'substitute-option__stock--ok' : 'substitute-option__stock--short'"
                        >stock {{ option.available }}{{ option.covers ? "" : " · no alcanza" }}</span
                      >
                    </span>
                  </template>
                  <template v-slot:selection="{ item: option }">
                    <span class="substitute-option">
                      <b class="substitute-option__code">{{ option.code }}</b>
                      <span class="substitute-option__desc">{{ option.description }}</span>
                      <span :class="option.covers ? 'substitute-option__stock--ok' : 'substitute-option__stock--short'"
                        >stock {{ option.available }}{{ option.covers ? "" : " · no alcanza" }}</span
                      >
                    </span>
                  </template>
                </v-select>
                <v-btn
                  x-small
                  color="blue darken-2"
                  dark
                  :loading="substituting === item.code"
                  @click="substitute(item)"
                >
                  Reemplazar
                </v-btn>
              </div>
              <span v-else-if="item.substitutes.length" class="text-caption">
                Sin stock suficiente:
                {{ item.substitutes.map((option) => `${option.code} (${option.available})`).join(", ") }}
              </span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-alert>
    <v-alert
      v-if="validation.date_risk.length"
      type="error"
      border="left"
      text
      dense
      class="mb-2"
    >
      <b>Fecha de despacho en riesgo:</b>
      {{ validation.date_risk.length }}
      {{ validation.date_risk.length === 1 ? "material de compra no alcanza" : "materiales de compra no alcanzan" }}
      a llegar para el {{ formatIsoDate(validation.desired_dispatch_date) }}:
      {{ validation.date_risk.map((item) => `${item.code} (llega ${formatIsoDate(item.earliest_date)})`).join(", ") }}.
      Puedes cambiar la fecha, reemplazar el material o enviar igual.
    </v-alert>
    <v-alert
      v-if="(validation.non_stock_materials || []).length"
      type="warning"
      border="left"
      text
      dense
      class="mb-2"
    >
      No son materiales de existencia (EXI-) y no se enviarán en la solicitud:
      <b>{{ validation.non_stock_materials.map((item) => item.code).join(", ") }}</b>
    </v-alert>
    <v-alert
      v-if="validation.materials_without_code"
      type="info"
      border="left"
      text
      dense
      class="mb-2"
    >
      {{ validation.materials_without_code }}
      {{ validation.materials_without_code === 1 ? "material no tiene" : "materiales no tienen" }}
      código SAP y no se enviarán en la solicitud.
    </v-alert>
    <v-expansion-panels v-if="validation.lines.length" flat class="mb-2">
      <v-expansion-panel class="review-panel">
        <v-expansion-panel-header>
          <span>
            <b>Revisión antes de enviar:</b> {{ validation.lines.length }} materiales ·
            {{ linesToPurchase }} con compra del faltante
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Categoría</th>
                <th>Tipo</th>
                <th>Código</th>
                <th>Descripción</th>
                <th class="text-right">Cantidad</th>
                <th class="text-right">Stock CD</th>
                <th class="text-right">T. entrega</th>
                <th>Destino</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in validation.lines" :key="`review-${line.code}`">
                <td>
                  <v-chip x-small :color="line.category === 'main_equipment' ? 'blue lighten-4' : 'grey lighten-3'">
                    {{ line.category === "main_equipment" ? "Equipo principal" : "Material" }}
                  </v-chip>
                </td>
                <td :class="line.is_standard ? '' : 'red--text text--darken-2 font-weight-bold'">
                  {{ line.is_standard ? "Estándar" : "No estándar" }}
                </td>
                <td>{{ line.code }}</td>
                <td>{{ line.description }}</td>
                <td class="text-right">{{ line.requested }}</td>
                <td
                  class="text-right font-weight-bold"
                  :class="line.shortfall > 0 ? 'red--text text--darken-2' : 'green--text text--darken-2'"
                >
                  {{ line.available }}
                </td>
                <td class="text-right">
                  {{ !(line.code in (leadTimes || {})) ? "…" : leadTimes[line.code] ? `${leadTimes[line.code]} días` : "-" }}
                </td>
                <td>
                  <v-chip x-small color="green lighten-4">ST</v-chip>
                  <v-chip v-if="line.shortfall > 0" x-small color="orange lighten-4" class="ml-1">
                    SC {{ line.shortfall }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="text-caption mb-0 mt-2">
            La ST reserva la cantidad completa; la SC compra solo el faltante. Stock según la
            última sincronización con SAP.
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { authUsuarioComputed } from "@/state/helpers";
import { escapeHtml } from "@/helpers/cubage";

// Pre-send checks of the cubicación (ms-cubicador /validate_document): missing main equipment,
// inactive materials, lines that will go to purchase and materials without code - so they can
// be fixed before sending instead of being found out at send time.
export default {
  props: {
    documentId: {
      type: Number,
      default: null,
    },
    // Header date (YYYY-MM-DD): purchase lines that can't make it show as at risk
    desiredDispatchDate: {
      type: String,
      default: null,
    },
    // Re-validates whenever the materials change (add, edit, delete, bulk upload)
    materials: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validation: null,
      showOverStock: false,
      debounce: null,
      // material code -> substitute code chosen in the dropdown (preselected: the first
      // substitute whose stock covers the line, from ms-cubicador)
      selectedSubstitutes: {},
      substituting: null,
      applyingRecommended: false,
    };
  },
  methods: {
    ...mapActions({
      validateCubage: "cubage/validateCubage",
      substituteMaterial: "cubage/substituteMaterial",
      applyRecommendedSubstitutes: "cubage/applyRecommendedSubstitutes",
      loadLeadTimes: "cubage/loadLeadTimes",
    }),
    formatIsoDate(value) {
      return value ? value.split("-").reverse().join("/") : "-";
    },
    async applyRecommended() {
      const count = this.validation.recommended_substitutions.length;
      const result = await this.$swal({
        icon: "question",
        title: `¿Aplicar ${count} sustituto(s) recomendado(s)?`,
        html: this.validation.recommended_substitutions
          .map((item) => `${escapeHtml(item.code)} → ${escapeHtml(item.substitute_code)}`)
          .join("<br>"),
        showCancelButton: true,
        confirmButtonText: "Aplicar",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      this.applyingRecommended = true;
      const resp = await this.applyRecommendedSubstitutes({
        document_id: this.documentId,
        user: this.user.email,
      });
      this.applyingRecommended = false;
      this.$swal({
        icon: resp.error ? "error" : "success",
        title: resp.error ? "Error" : "Excelente",
        text: resp.error || resp.message,
        position: "top-end",
        showConfirmButton: false,
        toast: true,
        timer: 3000,
        timerProgressBar: true,
      });
      if (!resp.error) this.$emit("substituted");
    },
    async refresh() {
      if (!this.documentId) {
        this.validation = null;
        return;
      }
      this.validation = await this.validateCubage({
        document_id: this.documentId,
        desired_dispatch_date: this.desiredDispatchDate || undefined,
      });
      if (this.validation) {
        this.loadLeadTimes({ codes: this.validation.lines.map((line) => line.code) });
      }
      const selected = {};
      if (this.validation) {
        this.validation.inactive_materials.forEach((item) => {
          const substitutes = item.substitutes || [];
          selected[item.code] =
            item.suggested_substitute || (substitutes.length ? substitutes[0].code : null);
        });
        // Short lines only offer covering substitutes; the suggested one is the best of them
        this.validation.over_stock.forEach((item) => {
          selected[item.code] = item.suggested_substitute || selected[item.code] || null;
        });
      }
      this.selectedSubstitutes = selected;
    },
    coveringSubstitutes(item) {
      return (item.substitutes || []).filter((substitute) => substitute.covers);
    },
    substituteText(substitute) {
      return `${substitute.code} - ${substitute.description} (stock ${substitute.available})${
        substitute.covers ? "" : " - no alcanza"
      }`;
    },
    async substitute(item) {
      const substituteCode = this.selectedSubstitutes[item.code];
      if (!substituteCode) return;
      const result = await this.$swal({
        icon: "question",
        title: `¿Reemplazar ${item.code} por ${substituteCode}?`,
        text: "Se cambia en todas las líneas de la cubicación, con las mismas cantidades.",
        showCancelButton: true,
        confirmButtonText: "Reemplazar",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      this.substituting = item.code;
      const resp = await this.substituteMaterial({
        document_id: this.documentId,
        code: item.code,
        substitute_code: substituteCode,
        user: this.user.email,
      });
      this.substituting = null;
      this.$swal({
        icon: resp.error ? "error" : "success",
        title: resp.error ? "Error" : "Excelente",
        text: resp.error || resp.message,
        position: "top-end",
        showConfirmButton: false,
        toast: true,
        timer: 3000,
        timerProgressBar: true,
      });
      if (!resp.error) {
        // The parent reloads the materials, which re-runs this validation
        this.$emit("substituted");
      }
    },
  },
  computed: {
    ...authUsuarioComputed,
    ...mapGetters({
      leadTimes: "cubage/leadTimes",
    }),
    // Lines with a known SAP lead time, highest first
    linesByLeadTime() {
      if (!this.validation) return [];
      return this.validation.lines
        .filter((line) => (this.leadTimes || {})[line.code])
        .map((line) => ({ ...line, days: this.leadTimes[line.code] }))
        .sort((a, b) => b.days - a.days);
    },
    maxLeadTime() {
      return this.linesByLeadTime[0] || null;
    },
    maxPurchaseLeadTime() {
      return this.linesByLeadTime.find((line) => line.shortfall > 0) || null;
    },
    // YYYY-MM-DD: today + the highest lead time among the lines that are purchased
    purchaseArrival() {
      if (!this.maxPurchaseLeadTime) return null;
      const today = new Date();
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Number(this.maxPurchaseLeadTime.days));
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    linesToPurchase() {
      return this.validation ? this.validation.lines.filter((line) => line.shortfall > 0).length : 0;
    },
    hasIssues() {
      const validation = this.validation;
      return Boolean(
        validation &&
          (validation.missing_main_equipment.length ||
            validation.inactive_materials.length ||
            validation.over_stock.length ||
            (validation.non_stock_materials || []).length ||
            validation.materials_without_code)
      );
    },
  },
  watch: {
    documentId: {
      immediate: true,
      handler() {
        this.refresh();
      },
    },
    desiredDispatchDate() {
      this.refresh();
    },
    materials() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.refresh(), 500);
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounce);
  },
};
</script>

<style scoped>
.review-panel {
  border: 1px solid #dde3ea;
  border-radius: 8px;
}
/* Default warning text (#fb8c00) on its own 12% tint is barely readable; the orange stays in
   the border, icon and tint */
.over-stock-alert >>> .v-alert__content {
  color: #3f2a14;
}
.substitute-select {
  max-width: 340px;
  font-size: 0.8rem;
}
.substitute-option {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.8rem;
  min-width: 0;
}
.substitute-option__code {
  color: #1f2937;
  font-weight: 600;
  white-space: nowrap;
}
.substitute-option__desc {
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.substitute-option__stock--ok {
  color: #1b5e20;
  font-weight: 500;
  white-space: nowrap;
}
.substitute-option__stock--short {
  color: #b42318;
  font-weight: 500;
  white-space: nowrap;
}
</style>
